import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { organisationsAPI, authAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

const EMPTY_ORG = { nom: '', slug: '', description: '' };
const EMPTY_USER = { nom: '', email: '', password: '', role: 'technicien' };

export default function Organisations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'org' | 'invite'
  const [form, setForm] = useState(EMPTY_ORG);
  const [userForm, setUserForm] = useState(EMPTY_USER);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    organisationsAPI.getAll().then(r => setOrgs(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSaveOrg = async () => {
    setSaving(true);
    try {
      if (current) {
        await organisationsAPI.update(current.id || current._id, form);
        toast.success('Organisation mise à jour');
      } else {
        await organisationsAPI.create(form);
        toast.success('Organisation créée');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleInvite = async () => {
    setSaving(true);
    try {
      await authAPI.register({ ...userForm, inviteOrgId: selectedOrg.id || selectedOrg._id });
      toast.success(`${userForm.nom} ajouté à ${selectedOrg.nom}`);
      setModal(null);
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Supprimer ${org.nom} et toutes ses données ?`)) return;
    try {
      await organisationsAPI.delete(org.id || org._id);
      toast.success('Organisation supprimée');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const handleToggle = async (org) => {
    await organisationsAPI.update(org.id || org._id, { actif: !org.actif });
    toast.success(org.actif ? 'Désactivée' : 'Activée');
    load();
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="page-title">Organisations</div>
          <div className="page-subtitle">Gestion des organisations et de leurs accès</div>
        </div>
        {user?.role === 'super_admin' && (
          <button className="btn btn-primary" onClick={() => { setForm(EMPTY_ORG); setCurrent(null); setModal('org'); }}>
            + Nouvelle organisation
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div>Chargement...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {orgs.map(org => (
            <div key={org.id || org._id} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: 20, boxShadow: 'var(--shadow-sm)',
              opacity: org.actif ? 1 : 0.6,
              borderLeft: `4px solid ${org.actif ? 'var(--primary)' : 'var(--text-muted)'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 8, background: 'var(--primary)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 16, flexShrink: 0,
                    }}>
                      {org.nom.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{org.nom}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>/{org.slug}</div>
                    </div>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: org.actif ? 'var(--green-bg)' : 'var(--border-light)',
                      color: org.actif ? 'var(--green)' : 'var(--text-muted)',
                    }}>
                      {org.actif ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {org.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>{org.description}</div>}
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      👥 <strong>{org.usersCount || 0}</strong> utilisateur(s)
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      📦 <strong>{org.equipementsCount || 0}</strong> équipement(s)
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-sm btn-outline" onClick={() => {
                    setSelectedOrg(org); setUserForm(EMPTY_USER); setModal('invite');
                  }}>
                    👤 Inviter
                  </button>
                  {user?.role === 'super_admin' && (
                    <>
                      <button className="btn btn-sm btn-outline" onClick={() => {
                        setForm({ nom: org.nom, slug: org.slug, description: org.description || '' });
                        setCurrent(org); setModal('org');
                      }}>✎</button>
                      <button className={`btn btn-sm ${org.actif ? 'btn-amber' : 'btn-green'}`} onClick={() => handleToggle(org)}>
                        {org.actif ? '⊘' : '◉'}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(org)}>✕</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {orgs.length === 0 && (
            <div className="empty-state"><div className="empty-icon">🏢</div>Aucune organisation</div>
          )}
        </div>
      )}

      {/* Modal Organisation */}
      {modal === 'org' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">{current ? 'Modifier organisation' : 'Nouvelle organisation'}</span>
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Nom *</label>
                  <input className="input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value, slug: f.slug || e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))} placeholder="Ex: T&P Partners Douala" />
                </div>
                <div className="form-group">
                  <label className="form-label">Identifiant (slug)</label>
                  <input className="input" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} placeholder="Ex: tp-partners-douala" />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="textarea" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description optionnelle" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSaveOrg} disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Invitation utilisateur */}
      {modal === 'invite' && selectedOrg && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <span className="modal-title">Inviter dans {selectedOrg.nom}</span>
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Nom complet *</label>
                  <input className="input" value={userForm.nom} onChange={e => setUserForm(f => ({ ...f, nom: e.target.value }))} placeholder="Prénom Nom" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="input" type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemple.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Mot de passe *</label>
                  <input className="input" type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 6 caractères" />
                </div>
                <div className="form-group">
                  <label className="form-label">Rôle</label>
                  <select className="select" value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="admin">Admin</option>
                    <option value="technicien">Technicien</option>
                    <option value="viewer">Lecture seule</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleInvite} disabled={saving}>{saving ? '...' : 'Inviter'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
